/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Send with the API", description = "Trigger a send from your own systems, supplying recipients and personalization on each call.", lastUpdatedDate = new Date(1786492800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Send with the API",
        description: "Trigger a send from your own systems, supplying recipients and personalization on each call.",
        lastUpdatedDate: "Wed Aug 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Set up the campaign",
          "depth": 0,
          "id": "set-up-the-campaign"
        }, {
          "value": "Trigger a send",
          "depth": 0,
          "id": "trigger-a-send"
        }, {
          "value": "Recipients",
          "depth": 0,
          "id": "recipients"
        }, {
          "value": "Read the response",
          "depth": 0,
          "id": "read-the-response"
        }, {
          "value": "Check a send",
          "depth": 0,
          "id": "check-a-send"
        }, {
          "value": "What you don't get",
          "depth": 0,
          "id": "what-you-dont-get"
        }, {
          "value": "Errors",
          "depth": 0,
          "id": "errors"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      code: "code",
      pre: "pre",
      span: "span",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and developers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A message built in ", _jsx(_components.a, {
                href: "/native-delivery/emails",
                children: "Build an email"
              }), " or ", _jsx(_components.a, {
                href: "/native-delivery/sms",
                children: "Build an SMS"
              }), ", and an API key"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "An API-triggered campaign sends when your own systems call it, with the recipients and their personalization supplied on each call. Use it for messages your application decides to send, such as an order confirmation or a password reset."
        })
      }), "\n", _jsx(_components.h2, {
        id: "set-up-the-campaign",
        children: "Set up the campaign"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Native Delivery → Campaigns"
          }), " and add a campaign."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set the ", _jsx(_components.strong, {
            children: "Schedule type"
          }), " to ", _jsx(_components.strong, {
            children: "API-triggered"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Build your message content."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Activate"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["An API-triggered campaign has no audience and no schedule. The ", _jsx(_components.strong, {
          children: "API trigger"
        }), " card takes the place of the schedule settings. It shows a ", _jsx(_components.strong, {
          children: "Sample payload"
        }), " for this campaign, generated with your message's own personalization variables, that you can copy and run."]
      }), "\n", _jsx(_components.p, {
        children: "A campaign carries one message. Dynamic content isn't supported in API-triggered campaigns."
      }), "\n", _jsx(_components.h2, {
        id: "trigger-a-send",
        children: "Trigger a send"
      }), "\n", _jsxs(_components.p, {
        children: ["Send a ", _jsx(_components.code, {
          children: "POST"
        }), " to the trigger endpoint with your API key as a bearer token. Create keys under ", _jsx(_components.strong, {
          children: "Settings → API keys"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["curl -X POST ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://api.hightouch.com/api/v1/campaigns/{campaignId}/trigger'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Authorization: Bearer YOUR_API_KEY'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Content-Type: application/json'"
          }), " \\\n  -d ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'{\n    \"recipients\": [\n      { \"handle\": \"sam@example.com\", \"properties\": { \"First name\": \"Sam\" } }\n    ]\n  }'"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Copy the sample payload from the ", _jsx(_components.strong, {
          children: "API trigger"
        }), " card instead of writing this by hand. It carries your campaign's ID, your workspace's regional host, and your message's actual variable names."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Personalization keys are the variable names as they appear in the editor, not the underlying column names. If two variables in one message share a name, the campaign won't activate until you rename one."
        })
      }), "\n", _jsx(_components.p, {
        children: "Up to 100 recipients per call. See the API reference for the full parameter list."
      }), "\n", _jsx(_components.h2, {
        id: "recipients",
        children: "Recipients"
      }), "\n", _jsx(_components.p, {
        children: "Each recipient is identified one of two ways."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Key"
            }), _jsx(_components.th, {
              children: "Use it when"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "handle"
              })
            }), _jsx(_components.td, {
              children: "You have the email address or phone number. Supply the personalization on the call."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsx(_components.td, {
              children: "You have the profile's primary key. Hightouch reads that profile's attributes and delivery address for you."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Sending by ", _jsx(_components.code, {
          children: "id"
        }), " requires a ", _jsx(_components.strong, {
          children: "Warehouse attribute cache"
        }), " under ", _jsx(_components.strong, {
          children: "Settings"
        }), ", plus a delivery address column mapped for the channel."]
      }), "\n", _jsxs(_components.p, {
        children: ["Personalization merges in order: the profile's own attributes, then any top-level ", _jsx(_components.code, {
          children: "properties"
        }), ", then that recipient's ", _jsx(_components.code, {
          children: "properties"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "read-the-response",
        children: "Read the response"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.code, {
          children: "200"
        }), " returns one result per recipient, in the order you sent them."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"results\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"handle\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"sam@example.com\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"status\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"accepted\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"sendId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"...\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"handle\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"not-an-address\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"status\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"rejected\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"reason\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"invalid_handle\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "accepted"
        }), " means the message is queued, not that it was sent. Subscription preferences, send limits, and deliverability are all checked afterward, so an accepted recipient can still be skipped."]
      }), "\n", _jsxs(_components.p, {
        children: ["Rejections are per recipient and don't fail the call. A batch where every recipient is rejected still returns ", _jsx(_components.code, {
          children: "200"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Reason"
            }), _jsx(_components.th, {
              children: "Meaning"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "invalid_handle"
              })
            }), _jsx(_components.td, {
              children: "Not a usable email address or phone number"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "missing_variables"
              })
            }), _jsxs(_components.td, {
              children: ["The message needs variables this call didn't supply. The names come back in ", _jsx(_components.code, {
                children: "missingVariables"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "invalid_id"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "id"
              }), " was empty"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "missing_handle_or_id"
              })
            }), _jsx(_components.td, {
              children: "Neither key was supplied"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Recipients sent by ", _jsx(_components.code, {
          children: "handle"
        }), " are validated as you call. Recipients sent by ", _jsx(_components.code, {
          children: "id"
        }), " are checked when the message is built, so their failures show up in the send status."]
      }), "\n", _jsx(_components.h2, {
        id: "check-a-send",
        children: "Check a send"
      }), "\n", _jsxs(_components.p, {
        children: ["Look up any accepted ", _jsx(_components.code, {
          children: "sendId"
        }), " to see what happened to it."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["curl ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'https://api.hightouch.com/api/v1/campaigns/{campaignId}/sends/{sendId}'"
          }), " \\\n  -H ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Authorization: Bearer YOUR_API_KEY'"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Status"
            }), _jsx(_components.th, {
              children: "Meaning"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sent"
              })
            }), _jsx(_components.td, {
              children: "Handed off for delivery, no result yet"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "delivered"
              })
            }), _jsx(_components.td, {
              children: "Delivery was confirmed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "bounced"
              })
            }), _jsx(_components.td, {
              children: "The address rejected it"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "failed"
              })
            }), _jsx(_components.td, {
              children: "The send failed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "dropped"
              })
            }), _jsxs(_components.td, {
              children: ["Hightouch didn't send it. Some drops carry a ", _jsx(_components.code, {
                children: "reason"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["A send appears here once it's been attempted, so a lookup immediately after triggering returns ", _jsx(_components.code, {
            children: "404"
          }), ". Retry instead of treating the first ", _jsx(_components.code, {
            children: "404"
          }), " as an error."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "what-you-dont-get",
        children: "What you don't get"
      }), "\n", _jsxs(_components.p, {
        children: ["An API-triggered campaign has no audience and no schedule, which also means it has no runs. You get all-time campaign metrics, but no per-send breakdown, no runs list, and no ", _jsx(_components.strong, {
          children: "Skipped"
        }), " or ", _jsx(_components.strong, {
          children: "Expired"
        }), " counts."]
      }), "\n", _jsx(_components.p, {
        children: "Subscription topics, unsubscribes, bounced addresses, quiet hours, and frequency caps are all still enforced, so a recipient who unsubscribed doesn't receive an API-triggered marketing message."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.a, {
            href: "/native-delivery/suppression-lists",
            children: "Suppression lists"
          }), " are the exception. They're applied when a campaign builds its audience from your warehouse, and an API-triggered send has no audience to build, so they don't apply here. Filter those recipients out before you call."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Whether a message ignores subscription topics depends on marking it transactional, not on how it's triggered. See ", _jsx(_components.a, {
          href: "/native-delivery/subscriptions-and-consent",
          children: "Subscriptions and consent"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "errors",
        children: "Errors"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Code"
            }), _jsx(_components.th, {
              children: "When"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "400"
              })
            }), _jsx(_components.td, {
              children: "The campaign isn't API-triggered, isn't active, has no published content, or carries more than one message"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "403"
              })
            }), _jsx(_components.td, {
              children: "Native Delivery isn't enabled for the workspace"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "404"
              })
            }), _jsx(_components.td, {
              children: "No campaign with that ID in this workspace"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "422"
              })
            }), _jsx(_components.td, {
              children: "The payload didn't validate: more than 100 recipients, missing fields, or unrecognized ones"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "429"
              })
            }), _jsx(_components.td, {
              children: "Too many requests. The limit is shared across all Hightouch API calls from your workspace"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["To test before activating, pass ", _jsx(_components.code, {
          children: "\"draft\": true"
        }), " and send from a draft campaign. Content and recipient validation still run, as do all send limits, so a draft send to an unsubscribed recipient is still skipped."]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/subscriptions-and-consent",
            children: "Subscriptions and consent"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/reporting",
            children: "Reporting"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
